import { useEffect } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { 
  $isParagraphNode, 
  ParagraphNode
} from 'lexical';
import { parseMarkdownTable, $createTableFromData } from './TableTransformers.js';

/**
 * Transform function that converts markdown table text to TableNodes
 * Following the official Lexical guide pattern for node transforms
 */
function markdownTableTransform(node, editor) {
  // Only process paragraph nodes that might contain table markdown
  if (!$isParagraphNode(node)) {
    return;
  }

  const textContent = node.getTextContent();
  
  // Quick check if this might be a table line
  if (!textContent.includes('|') || textContent.trim().length === 0) {
    return;
  }

  // Look for table pattern in current and next siblings
  let tableText = '';
  let nodesToReplace = [];
  let currentNode = node;
  
  // Collect potential table lines
  while (currentNode && $isParagraphNode(currentNode)) {
    const content = currentNode.getTextContent().trim();
    
    if (content.startsWith('|') && content.endsWith('|')) {
      tableText += (tableText ? '\n' : '') + content;
      nodesToReplace.push(currentNode);
      currentNode = currentNode.getNextSibling();
    } else if (tableText) {
      // We've collected some table lines, now check if we need to stop
      break;
    } else {
      // This node doesn't look like a table line
      return;
    }
  }

  // Parse the collected text as a markdown table
  const { rows, isValid } = parseMarkdownTable(tableText);
  
  if (!isValid || rows.length === 0) {
    return;
  }

  try {
    // Create the table node
    const tableNode = $createTableFromData(rows);
    
    // Replace the first node with the table
    if (nodesToReplace.length > 0) {
      nodesToReplace[0].replace(tableNode);
      
      // Remove the remaining nodes
      for (let i = 1; i < nodesToReplace.length; i++) {
        nodesToReplace[i].remove();
      }
    }
  } catch (error) {
    console.error('Error creating table from markdown:', error);
  }
}

/**
 * Plugin registration function following the official guide pattern
 * A plugin is a function that accepts LexicalEditor and returns cleanup function
 */
export function registerMarkdownTablePlugin(editor) {
  // Register transform for ParagraphNode to detect markdown tables
  const unregisterTransform = editor.registerNodeTransform(
    ParagraphNode, 
    (node) => markdownTableTransform(node, editor)
  );

  // Return cleanup function
  return () => {
    unregisterTransform();
  };
}

/**
 * React wrapper component for the plugin
 * This makes it easy to use in React applications
 */
export default function MarkdownTablePlugin() {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    return registerMarkdownTablePlugin(editor);
  }, [editor]);

  return null;
}
